import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
// FIX: Use named imports for Firebase v9+ modular SDK functions.
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { FIREBASE_AUTH, FIRESTORE } from '../firebase.config';
import { UserProfile } from '../models/user.model';
import { sanitizeData } from '../utils/firebase.utils';
import { getClientAccessState } from '../utils/client-access.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private zone = inject(NgZone);
  private router: Router = inject(Router);

  userProfile = signal<UserProfile | null | undefined>(undefined);

  isLoading = computed(() => this.userProfile() === undefined);
  isAuthenticated = computed(() => !!this.userProfile());
  currentUserRole = computed(() => this.userProfile()?.role ?? null);
  clientAccessState = computed(() => getClientAccessState(this.userProfile() ?? null));
  hasVerifiedClientAccess = computed(() => {
    const profile = this.userProfile();
    return profile?.role === 'Client' ? this.clientAccessState().hasAccess : false;
  });

  constructor() {
    // FIX: Call functions directly from import.
    onAuthStateChanged(FIREBASE_AUTH, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(FIRESTORE, `users/${user.uid}`);
          const userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            throw new Error(`User profile not found in Firestore for UID: ${user.uid}. This should not happen.`);
          }

          const profile = sanitizeData(userDoc.data()) as UserProfile;

          // If the user is an admin, force a token refresh to ensure custom claims are up-to-date.
          // This prevents permission errors if the admin performs an action immediately after login/signup.
          if (profile.role === 'Admin') {
            await user.getIdToken(true);
          }

          // Set default verification statuses if they don't exist
          if (profile.role === 'Client') {
            if (!profile.kycStatus) {
              profile.kycStatus = 'Action Required';
            }
            if (!profile.amlStatus) {
              profile.amlStatus = 'Action Required';
            }
          }

          this.zone.run(() => {
            this.userProfile.set(profile);
            // Redirect after successful login if the user is on the login page.
            const currentUrl = this.router.url;
            if (profile && (currentUrl === '/' || currentUrl.startsWith('/login'))) {
              const targetUrl = profile.role === 'Admin' ? '/admin' : '/client';
              this.router.navigate([targetUrl]);
            }
          });

        } catch (error) {
          console.error("Critical Auth Service Error: Failed to get user profile. The user might exist in Auth but not in Firestore. Signing out.", error);
          // FIX: Call functions directly from import.
          await signOut(FIREBASE_AUTH);
          this.zone.run(() => {
            this.userProfile.set(null);
          });
        }
      } else {
        this.zone.run(() => {
          this.userProfile.set(null);
        });
      }
    });
  }

  async login(email: string, password: string): Promise<void> {
    // FIX: Call functions directly from import.
    await signInWithEmailAndPassword(FIREBASE_AUTH, email, password);
    // onAuthStateChanged will handle reading the profile and redirecting.
  }

  async logout(): Promise<void> {
    // FIX: Call functions directly from import.
    await signOut(FIREBASE_AUTH);
    this.router.navigate(['/login']);
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    // FIX: Call functions directly from import.
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const sendPasswordResetEmail = httpsCallable(functions, 'sendPasswordResetEmail');
    const platformUrl = window.location.origin;
    await sendPasswordResetEmail({ email, platformUrl });
  }

  async verifyPasswordResetCode(code: string): Promise<string> {
    const { verifyPasswordResetCode } = await import('firebase/auth');
    return await verifyPasswordResetCode(FIREBASE_AUTH, code);
  }

  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    const { confirmPasswordReset } = await import('firebase/auth');
    await confirmPasswordReset(FIREBASE_AUTH, code, newPassword);
  }
}
